import type { User } from '@lensora/shared'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Spinner,
} from '@lensora/ui'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as React from 'react'


import { api, ApiError } from '@/lib/api'
import { useAuth } from '@/providers/auth-provider'

type Invitation = {
  id: string
  email: string
  role: 'admin' | 'member'
  expiresAt: string
  createdAt: string
}

const ROLES = [
  {
    value: 'member' as const,
    label: 'Member',
    description: 'Can view and use features. Cannot manage team or settings.',
  },
  {
    value: 'admin' as const,
    label: 'Admin',
    description: 'Can manage settings, team members, and all content.',
  },
]

// ─── Data hooks ────────────────────────────────────────────────

function useTeam() {
  return useQuery({
    queryKey: ['team'],
    queryFn: () => api.get<User[]>('/api/admin/tenants/team'),
  })
}

function useInvitations(enabled: boolean) {
  return useQuery({
    queryKey: ['team', 'invitations'],
    queryFn: () => api.get<Invitation[]>('/api/admin/tenants/team/invitations'),
    enabled,
  })
}

function useInviteMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ email, role }: { email: string; role: 'admin' | 'member' }) =>
      api.post('/api/admin/tenants/team/invite', { email, role }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['team', 'invitations'] }),
  })
}

function useRoleMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: 'admin' | 'member' }) =>
      api.patch(`/api/admin/tenants/team/${userId}/role`, { role }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['team'] }),
  })
}

function useStatusMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, disabled }: { userId: string; disabled: boolean }) =>
      api.patch(`/api/admin/tenants/team/${userId}/status`, { disabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['team'] }),
  })
}

function useRevokeMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/tenants/team/invitations/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['team', 'invitations'] }),
  })
}

// ─── Invite modal ──────────────────────────────────────────────
// Plain React + Tailwind modal — avoids Radix Dialog portal issues.

function InviteModal({ onClose }: { onClose: () => void }) {
  const [email, setEmail] = React.useState('')
  const [role, setRole] = React.useState<'admin' | 'member'>('member')
  const [error, setError] = React.useState<string | null>(null)
  const invite = useInviteMutation()

  // Close on Escape key
  React.useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    try {
      await invite.mutateAsync({ email, role })
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to send invite')
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-surface p-6 shadow-xl">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-text">Invite a team member</h3>
          <p className="mt-1 text-sm text-text-muted">
            They'll receive an email with a link to set up their account.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="invite-email">Email address</Label>
            <Input
              id="invite-email"
              type="email"
              placeholder="colleague@example.com"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Role</Label>
            <div className="space-y-2">
              {ROLES.map((r) => (
                <label
                  key={r.value}
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                    role === r.value
                      ? 'border-brand-primary bg-brand-primary/5'
                      : 'border-border hover:bg-surface-raised'
                  }`}
                >
                  <input
                    type="radio"
                    name="role"
                    value={r.value}
                    checked={role === r.value}
                    onChange={() => setRole(r.value)}
                    className="mt-0.5"
                  />
                  <div>
                    <p className="text-sm font-medium text-text">{r.label}</p>
                    <p className="text-xs text-text-muted">{r.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {error && <p className="text-sm text-feedback-error">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={invite.isPending}>
              {invite.isPending ? 'Sending…' : 'Send invite'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Member row ────────────────────────────────────────────────

const roleVariant = {
  owner: 'default',
  admin: 'secondary',
  member: 'outline',
  client: 'outline',
} as const

function MemberRow({
  member,
  currentUserId,
  canManage,
  isOwner,
}: {
  member: User
  currentUserId: string | null
  canManage: boolean
  isOwner: boolean
}) {
  const roleMutation = useRoleMutation()
  const statusMutation = useStatusMutation()
  const isSelf = member.id === currentUserId
  const isDisabled = !member.isActive

  return (
    <div className={`flex items-center justify-between px-6 py-4 ${isDisabled ? 'opacity-50' : ''}`}>
      <div className="flex items-center gap-3">
        <Avatar className="h-9 w-9">
          <AvatarImage src={member.avatarUrl ?? undefined} />
          <AvatarFallback>{(member.email ?? '?').charAt(0).toUpperCase()}</AvatarFallback>
        </Avatar>
        <div>
          <p className="text-sm font-medium text-text">
            {member.fullName ?? member.email}
            {isSelf && <span className="ml-2 text-xs text-text-muted">(you)</span>}
          </p>
          {member.fullName && <p className="text-xs text-text-muted">{member.email}</p>}
        </div>
      </div>

      <div className="flex items-center gap-2">
        {isDisabled && (
          <Badge variant="outline" className="text-text-muted">Disabled</Badge>
        )}
        <Badge variant={roleVariant[member.role]} className="capitalize">{member.role}</Badge>

        {canManage && !isSelf && member.role !== 'owner' && (
          <>
            <Button
              variant="ghost"
              size="sm"
              disabled={roleMutation.isPending}
              onClick={() =>
                roleMutation.mutate({
                  userId: member.id,
                  role: member.role === 'admin' ? 'member' : 'admin',
                })
              }
            >
              Make {member.role === 'admin' ? 'member' : 'admin'}
            </Button>

            {isOwner && (
              <Button
                variant="ghost"
                size="sm"
                disabled={statusMutation.isPending}
                onClick={() =>
                  statusMutation.mutate({ userId: member.id, disabled: !isDisabled })
                }
              >
                {isDisabled ? 'Enable' : 'Disable'}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ─── Page ──────────────────────────────────────────────────────

export function TeamPage() {
  const { data: members, isLoading } = useTeam()
  const auth = useAuth()
  const [showInvite, setShowInvite] = React.useState(false)

  const currentUser = auth.status === 'authenticated' ? auth.user : null
  const canManage = currentUser?.role === 'owner' || currentUser?.role === 'admin'
  const isOwner = currentUser?.role === 'owner'

  const { data: invitations } = useInvitations(canManage)
  const revoke = useRevokeMutation()

  const activeMembers = members?.filter((m) => m.isActive) ?? []
  const disabledMembers = members?.filter((m) => !m.isActive) ?? []

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      {showInvite && <InviteModal onClose={() => setShowInvite(false)} />}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-text">Team</h2>
          <p className="mt-1 text-sm text-text-muted">Manage who has access to your workspace.</p>
        </div>
        {canManage && (
          <Button onClick={() => setShowInvite(true)}>Invite member</Button>
        )}
      </div>

      {/* Active members */}
      <Card>
        <CardHeader>
          <CardTitle>Members</CardTitle>
          <CardDescription>
            {activeMembers.length} active member{activeMembers.length !== 1 ? 's' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent className="divide-y divide-border p-0">
          {activeMembers.map((member) => (
            <MemberRow
              key={member.id}
              member={member}
              currentUserId={currentUser?.id ?? null}
              canManage={canManage}
              isOwner={isOwner ?? false}
            />
          ))}
        </CardContent>
      </Card>

      {/* Disabled members — owner only */}
      {isOwner && disabledMembers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Disabled members</CardTitle>
            <CardDescription>
              {disabledMembers.length} member{disabledMembers.length !== 1 ? 's' : ''} cannot access this workspace
            </CardDescription>
          </CardHeader>
          <CardContent className="divide-y divide-border p-0">
            {disabledMembers.map((member) => (
              <MemberRow
                key={member.id}
                member={member}
                currentUserId={currentUser?.id ?? null}
                canManage={canManage}
                isOwner={isOwner}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Pending invitations */}
      {canManage && invitations && invitations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Pending invitations</CardTitle>
            <CardDescription>{invitations.length} awaiting response</CardDescription>
          </CardHeader>
          <CardContent className="divide-y divide-border p-0">
            {invitations.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between px-6 py-4">
                <div>
                  <p className="text-sm font-medium text-text">{inv.email}</p>
                  <p className="text-xs text-text-muted capitalize">
                    {inv.role} · expires {new Date(inv.expiresAt).toLocaleDateString()}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={revoke.isPending}
                  onClick={() => revoke.mutate(inv.id)}
                >
                  Revoke
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
